// Explicit symbol overrides for currencies used by our supported countries.
// This guarantees a symbol-like display even if Intl falls back to ISO codes.
const SYMBOL_OVERRIDES = {
  USD: "$",
  CAD: "C$",
  GBP: "£",
  AUD: "A$",
  NZD: "NZ$",

  EUR: "€",
  PLN: "zł",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  CHF: "CHF",

  CZK: "Kč",
  HUF: "Ft",
  RON: "lei",
  BGN: "лв",

  AED: "د.إ",
  ILS: "₪",
  TRY: "₺",

  MXN: "MX$",
  BRL: "R$",
  ARS: "AR$",
  ZAR: "R",

  BDT: "৳",
};

export function getCurrencySymbol(code) {
  const cur = String(code || "").toUpperCase();
  if (!cur) return null;

  // Prefer explicit overrides for currencies where Intl is inconsistent across locales.
  if (SYMBOL_OVERRIDES[cur]) return SYMBOL_OVERRIDES[cur];

  // Best-effort: ask Intl for a narrow symbol.
  // Note: Some currencies may still return the ISO code depending on locale support.
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: cur,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).formatToParts(0);

    const currencyPart = parts.find((p) => p.type === "currency");
    const sym = currencyPart?.value ? String(currencyPart.value).trim() : "";
    if (!sym) return null;

    // If Intl just echoes the code back, treat it as missing symbol.
    if (sym.toUpperCase() === cur) return null;

    return sym;
  } catch {
    return null;
  }
}

export function normalizeCurrencyRates(currencyPayload) {
  const rates = currencyPayload?.rates;
  if (!rates || typeof rates !== "object") return {};

  // Case A: { rates: { USD: {currency:'USD', rate:1}, BDT: {...} } }
  const direct = {};
  for (const [k, v] of Object.entries(rates)) {
    if (v && typeof v === "object" && typeof v.currency === "string" && v.rate != null) {
      const r = Number(v.rate);
      if (Number.isFinite(r) && r > 0) direct[v.currency] = r;
    } else {
      // Case B: { rates: { BD: {currency:'BDT', rate:...}, US: {currency:'USD', rate:...} } }
      if (v && typeof v === "object" && typeof v.currency === "string" && v.rate != null) {
        const r = Number(v.rate);
        if (Number.isFinite(r) && r > 0) direct[v.currency] = r;
      }
      // Some payloads may store plain numbers
      const asNum = Number(v);
      if (Number.isFinite(asNum) && asNum > 0 && /^[A-Z]{3}$/.test(k)) {
        direct[k] = asNum;
      }
    }
  }
  return direct;
}

export function convertAmount(amount, { from = "USD", to = "USD", rates = {} } = {}) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  if (from === to) return n;

  const fromRate = from === "USD" ? 1 : Number(rates[from]);
  const toRate = to === "USD" ? 1 : Number(rates[to]);

  // Rates are defined as: 1 USD = rate[currency]
  if (from !== "USD" && !(Number.isFinite(fromRate) && fromRate > 0)) return n;
  if (to !== "USD" && !(Number.isFinite(toRate) && toRate > 0)) return n;

  if (from === "USD") return n * toRate;
  if (to === "USD") return n / fromRate;
  return n * (toRate / fromRate);
}

export function formatMoney(amount, {
  to = "USD",
  from = "USD",
  rates = {},
  showCodeForNonSymbol = true,
} = {}) {
  const n = Number(amount);
  const safeInput = Number.isFinite(n) ? n : 0;

  const fmt = (val, code) => {
    const sym = getCurrencySymbol(code);
    const rounded = (Number.isFinite(val) ? val : 0).toFixed(2);
    if (sym) {
      // If the symbol is multi-letter (e.g., CHF, lei), add a space for readability.
      const needsSpace = /^[A-Za-z]{2,}$/.test(sym);
      return needsSpace ? `${sym} ${rounded}` : `${sym}${rounded}`;
    }
    return showCodeForNonSymbol ? `${code} ${rounded}` : rounded;
  };

  // If we can't actually convert (missing rates), don't lie with the target currency.
  if (from !== to) {
    const fromRate = from === "USD" ? 1 : Number(rates[from]);
    const toRate = to === "USD" ? 1 : Number(rates[to]);
    const canConvertFrom = from === "USD" || (Number.isFinite(fromRate) && fromRate > 0);
    const canConvertTo = to === "USD" || (Number.isFinite(toRate) && toRate > 0);
    if (!canConvertFrom || !canConvertTo) {
      return fmt(safeInput, from);
    }
  }

  const converted = convertAmount(safeInput, { from, to, rates });
  return fmt(converted, to);
}

export function guessCurrencyFromText(text) {
  if (!text) return null;
  const str = String(text);

  // Match a 3-letter currency code if present.
  const codeMatch = str.match(/\b([A-Z]{3})\b/);
  if (codeMatch) return codeMatch[1];

  // Heuristics for common formats
  if (str.includes("US $") || str.includes("US$") || str.includes("USD")) return "USD";
  if (str.includes("৳") || str.includes("BDT")) return "BDT";
  if (str.includes("€") || str.includes("EUR")) return "EUR";
  if (str.includes("£") || str.includes("GBP")) return "GBP";

  return null;
}
